import { AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import {
  bulkEmailToJobPosters,
  fileUploadToS3,
  IConsultant,
  IGlobalJobDTO,
  IJobConsultantInteractionDTO,
  uploadAttachment,
  askForResume,
  IEmailDTO,
  ITemplateDTO,
  getEmailTemplates,
  getUserOrTeamEmailTemplates,
} from 'wisestep-api';
import { BulkEmailForm, IUserDTO, TemplateListing, WsDrawer } from 'wisestep-base';

import { useUserContext, useWorkspaceContext } from '../../context';
import ActiveConsultantsTemplate from '../../features/activeConsultants/helper/ActiveConsultantsTemplate';
import hotJobsEmailTemplate from '../../features/importConsultants/helper/HotJobsEmailTemplate';
import { failed, success } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { openUrlInNewTab } from '../../utils/openUrlInNewTab';
import NoDataFoundCard from '../noDataFoundCard';
import { useSKUModalStore } from '../skuModal/SKUModalStore';

import { IEmailFormData } from './BulkEmail';
import {
  fetchJobOwnersWhoBlockedRecruiter,
  useFetchRecruiterSMTPAccounts,
  validateAllEmails,
} from './helper/apiCalls';

export enum mailType {
  ACTIVECONSULTANT = 'activeConsultant',
  CONSULTANT = 'consultant',
  GLOBALJOB = 'globalJob',
}

export interface IJobPostersBulkEmail<T> {
  toEmailList: { email: string; text: string }[];
  jobs: T[];
  consultants?: IConsultant[];
  template: string;
  open: boolean;
  handleClose: () => void;
  type?: mailType;
}

const JobPostersBulkEmail = <T extends IGlobalJobDTO>({
  toEmailList,
  jobs,
  consultants = [],
  template,
  open,
  handleClose,
  type = mailType.GLOBALJOB,
}: IJobPostersBulkEmail<T>) => {
  const user = useUserContext().userDTO as IUserDTO;

  const smtpAccounts = useFetchRecruiterSMTPAccounts().data;
  const setModal = useSKUModalStore((state) => state.setModal);
  const jobOwnersWhoBlockedRecruiter = fetchJobOwnersWhoBlockedRecruiter(
    smtpAccounts.length > 0 ? smtpAccounts[0].fromEmail : ''
  );
  const [isTemplateSelected, setIsTemplateSelected] = useState<boolean>(false);
  const [openTemplates, setOpenTemplates] = useState<boolean>(false);
  const [allTemplates, setAllTemplates] = useState<ITemplateDTO[]>([]);
  const [emailFormData, setEmailFormData] = useState<IEmailFormData>({
    attachments: [],
    jobCode: null,
    job: null,
    totalCount: 0,
    selectedTemplateBody: '',
    selectedTemplateSubject: '',
    selectedTemplateName: '',
    ccList: [],
    bccList: [],
  });
  toEmailList = toEmailList.filter((email) => !jobOwnersWhoBlockedRecruiter.includes(email.email));

  const [attachments, setAttachments] = useState<
    {
      attachementName: string;
      attachementURL: string;
    }[]
  >([]);

  const handleOpenTemplates = () => setOpenTemplates(true);

  const handleCloseTemplates = () => setOpenTemplates(false);
  const defaultTemplate = hotJobsEmailTemplate(smtpAccounts, consultants[0]);

  const setNotification = useNotificationStore((state) => state.setNotification);

  const workspaceContext = useWorkspaceContext();

  const manageSmptAccounts = () =>
    openUrlInNewTab(`https://${workspaceContext.link}.wisestep.org/email-settings/`);

  const upload = async (file: File) => {
    let response;
    try {
      response = await uploadAttachment({ fileName: file.name, fileType: file.type });
    } catch (err: any) {
      setNotification(failed(err.message));
    }
    setAttachments((prev) => [
      ...prev,
      {
        attachementName: file.name,
        attachementURL: response.data.preSignedUrl.split('?')[0],
      },
    ]);
    try {
      await fileUploadToS3(file, response.data.preSignedUrl);
    } catch (err: any) {
      setNotification(failed(err.message));
    }
  };

  const send = async (data) => {
    const payload: IJobConsultantInteractionDTO[] = [];
    const payloadForActiveConsultants: IEmailDTO = {
      from: '',
      to: [],
      cc: [],
      bcc: [],
      subject: '',
      body: '',
      attachements: [],
    };
    data.to.forEach((email, i) => {
      if (consultants.length > 0 && type === mailType.GLOBALJOB) {
        consultants.forEach((consultant) => {
          payload.push({
            consultantEmail: consultant.email,
            consultantId: consultant.id,
            consultantJobTitle: consultant.jobTitle,
            consultantLocation: consultant.location,
            consultantName: consultant.name,
            consultantOrganization: consultant.organization,
            consultantPhoneNumber: consultant.phoneNumber,
            consultantPreferredLocations: consultant.preferredLocations,
            consultantRelocationPreference: consultant.relocationPreference,
            consultantSkills: consultant.skills,
            consultantTotalMonthsExperience: consultant.totalMonthsExperience,
            consultantTotalYearsExperience: consultant.totalYearsExperience,
            consultantUniqueId: consultant.uniqueId,
            consultantVisaStatus: consultant.visaStatus,
            consultantWillingToRelocate: consultant.willingToRelocate,
            emailDTO: {
              attachements: attachments,
              bcc: data.bcc,
              body: data.body,
              cc: data.cc,
              from: data.from,
              subject: data.subject,
              to: data.to.map((email: string) => {
                const unmaskedEmail: string[] = [];
                unmaskedEmail.push(
                  toEmailList.find((emailObj) => emailObj.text === email)?.email ?? email
                );
                return unmaskedEmail.length > 0 ? unmaskedEmail[0] : '';
              }),
            },
            jobCompany: jobs[i].company,
            jobDescription: jobs[i].description,
            jobExperienceRequirementsInYears: jobs[i].experienceRequirementsInYears,
            jobId: jobs[i].id ?? '',
            jobLocations: jobs[i].locations,
            jobSenderEmail: jobs[i].latestSenderEmail,
            jobSkills: jobs[i].skills,
            jobSource: jobs[i].sourceOfJob,
            jobTitle: jobs[i].title ?? '',
            jobUrl: jobs[i].applyURL,
          });
        });
      } else if (type === mailType.ACTIVECONSULTANT) {
        Object.assign(payloadForActiveConsultants, {
          bcc: data.bcc,
          body: data.body,
          cc: data.cc,
          from: data.from,
          fromName: smtpAccounts.find((smtpAccount) => smtpAccount.fromEmail === data.from)
            ?.fromName,
          subject: data.subject,
          to: data.to.map((email: string) => {
            const unmaskedEmail: string[] = [];
            unmaskedEmail.push(
              toEmailList.find((emailObj) => emailObj.text === email)?.email ?? email
            );
            return unmaskedEmail.length > 0 ? unmaskedEmail[0] : '';
          }),
        });
      } else {
        payload.push({
          emailDTO: {
            attachements: attachments,
            bcc: data.bcc,
            body: data.body,
            cc: data.cc,
            from: data.from,
            subject: data.subject,
            to: data.to.map((email: string) => {
              const unmaskedEmail: string[] = [];
              unmaskedEmail.push(
                toEmailList.find((emailObj) => emailObj.text === email)?.email ?? email
              );
              return unmaskedEmail.length > 0 ? unmaskedEmail[0] : '';
            }),
          },
          jobCompany: jobs[i].company,
          jobDescription: jobs[i].description,
          jobExperienceRequirementsInYears: jobs[i].experienceRequirementsInYears,
          jobId: jobs[i].id ?? '',
          jobLocations: jobs[i].locations,
          jobSenderEmail: jobs[i].latestSenderEmail,
          jobSkills: jobs[i].skills,
          jobSource: jobs[i].sourceOfJob,
          jobTitle: jobs[i].title ?? '',
          jobUniqueId: jobs[i].uniqueId,
          jobUrl: jobs[i].applyURL,
        });
      }
    });
    try {
      if (type === mailType.GLOBALJOB) await bulkEmailToJobPosters(payload[0]);
      else if (type === mailType.ACTIVECONSULTANT) await askForResume(payloadForActiveConsultants);
      setNotification(success('Emails sent successfully!'));
      handleClose();
    } catch (err: any) {
      const error = err as AxiosError;
      if (error.response?.status === 402) {
        error.response.data && setModal({ object: err.response.data, type: 'count' });
      }
      setNotification(failed(error.message));
    }
  };

  const addTemplate = {
    [mailType.ACTIVECONSULTANT]: ActiveConsultantsTemplate(smtpAccounts, consultants[0]),
    [mailType.GLOBALJOB]: defaultTemplate,
    [mailType.CONSULTANT]: 'Empty Template',
  };

  const getSubject = {
    [mailType.ACTIVECONSULTANT]: '',
    [mailType.GLOBALJOB]: !isEmpty(consultants)
      ? `${consultants[0].jobTitle} | ${consultants[0].availability ?? 'Available Immediately'}`
      : '',
    [mailType.CONSULTANT]: !isEmpty(consultants)
      ? `${consultants[0].jobTitle} | ${consultants[0].availability ?? 'Available Immediately'}`
      : '',
  };

  const openTemplateList = () => {
    getAllTemplates();
    handleOpenTemplates();
  };

  const getTemplateId = (selectedTemplateId: string) => {
    allTemplates.map(async (template) => {
      if (template.id === selectedTemplateId) {
        setIsTemplateSelected(true);

        setEmailFormData((prev) => ({
          ...prev,
          selectedTemplateName: template.name ?? '',
          selectedTemplateBody: template.body ?? '',
          selectedTemplateSubject: template.subject ?? '',
          ccList: template.cc ?? [],
          bccList: template.bcc ?? [],
        }));
      }
    });
    handleOpenTemplates();
  };
  const getAllTemplates = async () => {
    const defaultTemplateResponse = await getEmailTemplates();
    const userOrTeamTemplateResponse = await getUserOrTeamEmailTemplates();
    const allTemplates = [...defaultTemplateResponse.data, ...userOrTeamTemplateResponse.data];
    const favouredTemplates: ITemplateDTO[] = [];
    const unFavouredTemplates: ITemplateDTO[] = [];
    allTemplates.forEach((template: ITemplateDTO) => {
      if (
        user.email &&
        template.favoredRecruitersByTemplate &&
        template?.favoredRecruitersByTemplate?.indexOf(user.email) > -1
      )
        favouredTemplates.push(template);
      else unFavouredTemplates.push(template);
    });
    setAllTemplates(allTemplates);
  };

  const removeSelectedTemplate = () => {
    setEmailFormData((prev) => ({
      ...prev,
      selectedTemplateName: '',
      selectedTemplateBody: '',
      selectedTemplateSubject: '',
    }));
    setIsTemplateSelected(false);
  };

  return (
    <WsDrawer open={open} onClose={handleClose} title={'Share Job(s)'} style={{ zIndex: '1300' }}>
      {smtpAccounts.length > 0 ? (
        <BulkEmailForm
          toEmailList={toEmailList.map((obj) => obj.text)}
          ccList={emailFormData.ccList}
          bccList={emailFormData.bccList}
          fromEmailList={smtpAccounts.map((account) => account.fromEmail)}
          jobTitle={null}
          jobCode={null}
          templateName={emailFormData.selectedTemplateName}
          template={
            isTemplateSelected
              ? emailFormData.selectedTemplateBody
              : !isEmpty(template)
              ? template
              : addTemplate[type]
          }
          subjectTemplate={
            isTemplateSelected ? emailFormData.selectedTemplateSubject : getSubject[type]
          }
          changeJob={() => {}}
          manageSenderAccounts={manageSmptAccounts}
          openTemplatesList={openTemplateList}
          removeSelectedTemplate={removeSelectedTemplate}
          uploadAttachment={upload}
          handleAttachmentError={() =>
            setNotification(failed('The file size must be less than 25MB!'))
          }
          submitAction={send}
          handleClose={handleClose}
          validateEmails={validateAllEmails}
          isJobPosterEmail={true}
        />
      ) : (
        <NoDataFoundCard text={'No configured SMTP Accounts found'} />
      )}
      <WsDrawer
        size="large"
        title={'Select a Template'}
        iconType="cross"
        open={openTemplates}
        onClose={handleCloseTemplates}
        style={{ zIndex: '1302' }}
      >
        <TemplateListing
          templates={allTemplates}
          handleClose={handleCloseTemplates}
          getTemplateId={getTemplateId}
        />
      </WsDrawer>
    </WsDrawer>
  );
};

export default JobPostersBulkEmail;
